import { FunctionComponent, SVGProps } from "react";

import { DealStageStatus, MetricsExtractionStatus } from "@enums/common.enum";

export type DocumentStatus = "failed" | "initial" | "pending_approval" | "processing" | "success";

export type DealSourceType = {
  id: string;
  title: string;
  is_default: boolean;
};

export type DealSourceTypesResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: DealSourceType[];
};

export type DealStage = {
  color: string;
  id: string;
  order: number;
  status: DealStageStatus;
  title: string;
  group: string;
};

export type FundStatus = "Open" | "Closed";

export type OpportunityStageInfo = {
  id: string;
  title: string;
  color: string;
};

export type Deal = {
  id: string;
  title: string;
  stage: DealStage;
  company?: string;
  company_logo?: string;
  company_data?: {
    id: string;
    logo_url: string;
    title: string;
    website: string;
  };
  description: string;
  attachment?: Partial<Document>;
  slug: string;
  team: string[];
  deal_contacts?: any;
  is_approval_needed?: boolean;
  hidden: boolean;
  object_type?: string;
  created: string;
};

export type Document = {
  created: string;
  file: string;
  id: string;
  is_memo: boolean;
  status: DocumentStatus;
  document_type: "IC Memo" | "CIM" | "Document";
  metrics_extraction_is_silent: boolean;
  metrics_extraction_status?: MetricsExtractionStatus;
  metrics_extraction_id?: string;
  modified: string;
  type: string;
  user: User;
  file_name: string;
  summary: string | null;
  file_preview?: string;
  name?: string;
};

export type Company = {
  id?: string;
  is_primary: boolean;
  company: {
    type: {
      id: string;
      subtype: string | null;
      title: string;
    };
    website: string;
    id: string;
    title: string;
    logo_url?: string;
    deals?: {
      id: string;
      stage: DealStage;
      title: string;
    }[];
    is_suggested: boolean;
  };
  relation?: string;
  is_key_person: boolean;
  title: string;
  website: string;
};

export type JSONContent = {
  type?: string;
  attrs?: Record<string, any>;
  content?: JSONContent[];
  marks?: {
    type: string;
    attrs?: Record<string, any>;
    [key: string]: any;
  }[];
  text?: string;
  [key: string]: any;
};

export type CallToActionConfig = {
  additionalClasses?: string;
  buttonTypeOverride?: "PRIMARY" | "SECONDARY" | "CANCEL" | "TRANSPARENT" | "DANGER";
  disabled?: boolean;
  id?: string;
  label: string;
  type?: "button" | "reset" | "submit";
  callback: VoidFunction;
  PrefixIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  SuffixIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
};

export type User = {
  avatar_url?: string;
  created?: string;
  email: string;
  external?: boolean;
  first_name?: string;
  full_name?: string;
  id: string;
  last_name?: string;
  organization?: Organization;
  grants?: Grant[];
  tags?: UserTag[];
  title: UserTitle | null;
  primary_company?: Company | null;
  is_active?: boolean;
  is_admin?: boolean;
  is_superuser?: boolean;
  status?: "Active" | "Pending";
  membership?: string;
  timezone?: string;
};

export type Organization = {
  id: string;
  created?: string;
  demo_expiry: string | null;
  description: string;
  domains: string[];
  logo_url: string;
  shared_meeting_notes: boolean;
  slug: string;
  title: string;
  type: "Paid" | "Demo" | "Expired";
  manual_sync_required: boolean;
  settings: OrganizationSettings;
};

export type OrganizationSettings = {
  emails_thread_auto_subscribe: boolean;
  default_stage_label?: string;
};

export enum GrantProvider {
  GOOGLE = "Google",
  OUTLOOK = "Outlook",
}

export enum GrantStatus {
  VALID = "Valid",
  EXPIRED = "Expired",
}

export type Grant = {
  grant_id: string;
  id: string;
  provider: GrantProvider;
  username: string;
  status: GrantStatus;
  meetings_synced: boolean;
  people_synced: boolean;
};

export type UserTag = {
  email: string;
  id: string;
  name?: string;
};

export type UserTitle = {
  id: string;
  title: string;
};

export type UnrecognizedUser = {
  id: string;
  first_name: string;
  last_name: string;
};

export interface UserForDropdown extends User {
  value: string;
  label: string;
  type?: "team members" | "people" | null;
}

export type CollaborativeTokens = {
  access: string;
  refresh: string;
};
